import React, { useEffect } from 'react';
import { Input, AutoComplete, Spin, Typography, Row, Col } from 'antd';
import { removeUnnecessaryCommas } from '../../helpers/common';
import { alertMessage } from '../../common/Common';
import Location from '../../../assets/images/Location_v2.svg';
import { SearchIcon, WarehouseIcon } from '../../common/CustomIcon';
import useAddressAutocomplete from '../../common/useAddressAutocomplete';

const { Option, OptGroup } = AutoComplete;
const { Text } = Typography;

// v1/v2 address picker. Now delegates ALL autocomplete/session/details logic to
// the shared `useAddressAutocomplete` hook (single source, so the Places cost
// optimization + `show_detailed_address_suggestions` org flag apply here too).
// The antd rendering, props and onSelect contract are unchanged.
function SearchAddress_v2(props) {
    const {
        hidePredefined = false,
        hideWebResults = false,
        doesAddressExist,
        linehaul_code,
        linehaul_label,
        address = "",
        orderTypeConfig = {},
        selectedWarehouseId = "",
        // Warehouse-only mode (standalone-linehaul destination): show only warehouse
        // suggestions — filter predefined to l_type 'WH' and drop free-form web results.
        warehousesOnly = false,
    } = props;

    const {
        searchText,
        setSearchText,
        results: filteredOptions,
        loading,
        onSearch,
        reset,
        resolveSelection,
    } = useAddressAutocomplete({
        hidePredefined,
        hideWebResults: hideWebResults || warehousesOnly,
        orderTypeConfig,
        selectedWarehouseId,
        linehaul_code,
        linehaul_label,
        warehousesOnly,
    });

    useEffect(() => {
        if (!doesAddressExist) { reset(); }
    }, [doesAddressExist, reset]);

    useEffect(() => {
        if (address) { setSearchText(address); }
    }, [address, setSearchText]);

    const format_l_address = (a) => a?.l_address
        ? a.l_address.address_line1 + ", " + a.l_address.address_line2 + ", " + a.l_address.city + ", " + a.l_address.state + ", " + a.l_address.zipcode + ", " + a.l_address.country
        : "";
    // Readable label for the input after selection: parsed address if present
    // (predefined / detailed-mode / resolved), else the prediction description.
    const rowLabel = (a) => removeUnnecessaryCommas(format_l_address(a) || a?.address || "");

    const handleSelect = async (value) => {
        const selected = (filteredOptions || []).find((a) => a.id === value);
        if (!selected) return;
        setSearchText(rowLabel(selected)); // immediate readable value (avoids showing the id)
        // Cost-optimized flow: web rows carry only the prediction; fetch Place
        // Details for just the chosen row here. No-op for predefined / detailed rows.
        const resolved = await resolveSelection(selected);
        // resolveSelection returns null when a web row's Place Details can't be
        // fetched — surface it instead of committing an address with no l_address.
        if (!resolved) {
            alertMessage(["Couldn't fetch address details. Please try again."], "error", 3);
            return;
        }
        props.onSelect(resolved);
        setSearchText(rowLabel(resolved));
    };

    const generateOptions = (addresses, formatFunction) =>
        addresses.map((addr) => (
            <Option key={addr.id} value={addr.id}>
                <Row type='flex' gutter={16} align='middle' className='search_address_option_bar'>
                    <Col>
                        {addr.l_type === "WH" ? (
                            <WarehouseIcon />
                        ) : (
                            <img className="searchAddressLocation" src={Location} alt="location-icon" />
                        )}
                    </Col>
                    <Col>
                        <Text type='strong'>{addr.name}</Text>
                        <Text type="secondary">{formatFunction(addr)}</Text>
                    </Col>
                </Row>
            </Option>
        ));

    const predefinedStopsOptions = generateOptions(
        (filteredOptions || []).filter((a) => a.type === 'predefined'),
        format_l_address
    );
    const webResultsOptions = generateOptions(
        (filteredOptions || []).filter((a) => a.type === 'web'),
        (a) => a.address
    );

    return (
        <div className='searchAddress_v2' data-testid="google-address-dropdown">
            <AutoComplete
                className="certain-category-search"
                dropdownClassName="certain-category-search-dropdown search-result-dropdown"
                dropdownMatchSelectWidth={false}
                dropdownStyle={{ width: 300 }}
                size="default"
                style={{ width: '100%' }}
                value={searchText}
                dataSource={
                    loading ? (
                        [<Option key="loading" disabled><Spin /> Fetching addresses</Option>]
                    ) : (
                        [
                            !hidePredefined && (
                                <OptGroup key="predefined" label="Predefined">
                                    {predefinedStopsOptions.length === 0 ? (
                                        <Option key="no-results" disabled>No results found</Option>
                                    ) : (
                                        predefinedStopsOptions
                                    )}
                                </OptGroup>
                            ),
                            !(hideWebResults || warehousesOnly) && (
                                <OptGroup key="web" label="Web Results">
                                    {webResultsOptions.length === 0 ? (
                                        <Option key="no-results" disabled>No results found</Option>
                                    ) : (
                                        webResultsOptions
                                    )}
                                </OptGroup>
                            ),
                        ].filter(Boolean)
                    )
                }
                onChange={(value) => { if (!value || value.length === 0) { reset(); } }}
                onSearch={onSearch}
                onSelect={handleSelect}
                placeholder={<span className='searchAdd_v2_placeholder'>Start typing address</span>}
                optionLabelProp="value"
            >
                <Input prefix={<SearchIcon />} allowClear />
            </AutoComplete>
        </div>
    );
}

export default SearchAddress_v2;
